import React, { useEffect, useRef, useState } from 'react';
import { Button, Checkbox, Form, Icon, Message, Modal } from 'semantic-ui-react';
import { useTranslation } from 'react-i18next';
import { API } from '../helpers';
import {
  RECORD_EXPORT_API_V1023, localDateV1023, localDateTimeV1023,
  recordExportRangeV1023, recordExportTextV1023,
} from './recordExportHelpersV1023';
import './recordExportV1023.css';

export default function RecordExportV1023({ kind = 'logs', scope = 'ordinary', accountId = '', admin = false, filters = {} }) {
  const { i18n } = useTranslation();
  const text = (key) => recordExportTextV1023(i18n.language, key);
  const [open, setOpen] = useState(false);
  const [account, setAccount] = useState('');
  const [organizations, setOrganizations] = useState([]);
  const [organizationError, setOrganizationError] = useState(false);
  const [organizationLoading, setOrganizationLoading] = useState(false);
  const [preset, setPreset] = useState('today');
  const [dates, setDates] = useState({});
  const [detailed, setDetailed] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState({});
  const [job, setJob] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const generation = useRef(0);
  const jobRef = useRef(null);
  const timer = useRef(null);
  const opener = useRef(null);
  const alive = useRef(true);
  const identity = `${kind}:${scope}:${accountId}`;
  const cancelBackground = () => {
    generation.current++;
    clearTimeout(timer.current);
    const current = jobRef.current;
    jobRef.current = null;
    if (current && ['queued', 'running'].includes(current.status))
      API.delete(`${RECORD_EXPORT_API_V1023}/${current.id}`).catch(() => {});
  };
  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; cancelBackground(); };
    // Requests are invalidated on identity change/unmount, never reused across organizations.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [identity]);
  useEffect(() => {
    setOpen(false); setJob(null); setBusy(false); setError('');
  }, [identity]);
  useEffect(() => {
    if (!open) return undefined;
    const container = document.getElementById(`record-export-dialog-${kind}`);
    const items = () => Array.from(container?.querySelectorAll('button,input,select,[tabindex]') || [])
      .filter((element) => !element.disabled && element.tabIndex >= 0 && element.getClientRects().length);
    const focus = setTimeout(() => items()[0]?.focus(), 0);
    const trap = (event) => {
      if (event.key !== 'Tab') return;
      const list = items(), index = list.indexOf(document.activeElement);
      if (list.length && (index < 0 || (event.shiftKey ? index === 0 : index === list.length - 1))) {
        event.preventDefault(); list[event.shiftKey ? list.length - 1 : 0].focus();
      }
    };
    document.addEventListener('keydown', trap);
    return () => {
      clearTimeout(focus); document.removeEventListener('keydown', trap);
      if (opener.current?.isConnected) opener.current.focus();
    };
  }, [open, kind]);

  const accept = (value) => {
    if (!value?.data?.success) throw new Error(value?.data?.message || 'export_unavailable');
    return value.data.data;
  };
  const errorCode = (e) => e?.response?.data?.message || e?.message || 'export_unavailable';
  const loadOrganizations = async (revision) => {
    setOrganizationLoading(true); setOrganizationError(false);
    try {
      const rows = accept(await API.get('/api/log/organizations', { params: { customer_scope: scope } }));
      if (generation.current === revision && alive.current) setOrganizations(Array.isArray(rows) ? rows : []);
    } catch {
      if (generation.current === revision && alive.current) setOrganizationError(true);
    } finally {
      if (generation.current === revision && alive.current) setOrganizationLoading(false);
    }
  };
  const download = async (value, revision) => {
    const response = await API.get(`${RECORD_EXPORT_API_V1023}/${value.id}/download`, { responseType: 'blob' });
    if (generation.current !== revision || !alive.current) return;
    if (!response?.data || !String(response.headers?.['content-disposition'] || '').includes('attachment')) {
      throw new Error('export_not_ready');
    }
    const url = URL.createObjectURL(response.data);
    const link = document.createElement('a');
    link.href = url; link.download = `${kind}-${value.id}.json`;
    document.body.appendChild(link); link.click(); link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };
  const poll = async (id, revision) => {
    try {
      const next = accept(await API.get(`${RECORD_EXPORT_API_V1023}/${id}`));
      if (generation.current !== revision || !alive.current) return;
      jobRef.current = next; setJob(next);
      if (next.status === 'completed') {
        await download(next, revision);
        if (generation.current === revision) setBusy(false);
      } else if (['queued', 'running'].includes(next.status)) {
        timer.current = setTimeout(() => poll(id, revision), 2500);
      } else throw new Error(next.error || 'export_not_ready');
    } catch (e) {
      if (generation.current === revision && alive.current) { setError(errorCode(e)); setBusy(false); }
    }
  };
  const create = async (input) => {
    const revision = ++generation.current;
    setBusy(true); setError(''); setJob(null);
    try {
      const next = accept(await API.post(RECORD_EXPORT_API_V1023, input));
      if (generation.current !== revision || !alive.current) {
        await API.delete(`${RECORD_EXPORT_API_V1023}/${next.id}`);
        return;
      }
      jobRef.current = next; setJob(next);
      await poll(next.id, revision);
    } catch (e) {
      if (generation.current === revision && alive.current) { setError(errorCode(e)); setBusy(false); }
    }
  };
  const begin = (event) => {
    opener.current = event.currentTarget;
    clearTimeout(timer.current);
    const revision = ++generation.current;
    jobRef.current = null; setJob(null); setError(''); setBusy(false);
    setOpen(true); setAccount(String(accountId || '')); setDetailed(false); setPreset('today');
    const now = new Date();
    setDates({ day: localDateV1023(now), month: localDateV1023(now).slice(0, 7),
      start: `${localDateV1023(now)}T00:00`, end: localDateTimeV1023(now) });
    setSelectedFilters({ ...filters });
    if (kind === 'ledger') create({ kind, scope: '', account_id: String(accountId || ''), detailed: false });
    else if (admin) loadOrganizations(revision);
  };
  const close = async () => {
    ++generation.current; clearTimeout(timer.current);
    const current = jobRef.current;
    if (current && ['queued', 'running'].includes(current.status)) {
      setBusy(true);
      try { accept(await API.delete(`${RECORD_EXPORT_API_V1023}/${current.id}`)); }
      catch (e) { setError(errorCode(e)); setBusy(false); return; }
    }
    jobRef.current = null; setJob(null); setBusy(false); setOpen(false);
  };
  const submit = () => {
    const range = recordExportRangeV1023(preset, dates);
    if (!range) { setError('invalid'); return; }
    create({ kind, scope, account_id: account, detailed, ...range, filters: selectedFilters });
  };
  const retry = () => {
    if (jobRef.current && ['queued', 'running', 'completed'].includes(jobRef.current.status)) {
      setBusy(true); setError(''); poll(jobRef.current.id, generation.current);
    } else if (kind === 'ledger') create({ kind, scope: '', account_id: String(accountId || ''), detailed: false });
    else { jobRef.current = null; setJob(null); setError(''); }
  };
  const working = busy || ['queued', 'running'].includes(job?.status);
  return (
    <>
      <Button type='button' size='small' onClick={begin} data-testid={`record-export-${kind}`}>
        <Icon name='download' />{text(kind === 'ledger' ? 'ledger' : 'download')}
      </Button>
      <Modal open={open} size='small' onClose={close} closeOnDimmerClick={!working} closeOnEscape={!working}
        className='record-export-v1023' id={`record-export-dialog-${kind}`}>
        <Modal.Header>{text(kind === 'ledger' ? 'ledgerTitle' : 'title')}</Modal.Header>
        <Modal.Content>
          {kind === 'logs' && !job && (
            <Form onSubmit={(event) => { event.preventDefault(); if (!busy) submit(); }}>
              <Form.Field>
                <label htmlFor='record-export-organization'>{text('organization')}</label>
                <select id='record-export-organization' value={account} disabled={busy || !admin || organizationLoading}
                  onChange={(event) => setAccount(event.target.value)}>
                  <option value=''>{text(admin ? 'all' : 'own')}</option>
                  {account && !organizations.some((row) => String(row.account_id ?? row.id) === account) &&
                    <option value={account}>{account}</option>}
                  {organizations.map((row) => <option key={row.account_id ?? row.id} value={String(row.account_id ?? row.id)}>{row.name || row.account_name || String(row.account_id ?? row.id)}</option>)}
                </select>
              </Form.Field>
              {organizationError && <Message negative>
                {text('organization_failed')} <Button type='button' size='mini' onClick={() => loadOrganizations(generation.current)}>{text('retry')}</Button>
              </Message>}
              <Form.Field>
                <label htmlFor='record-export-range'>{text('range')}</label>
                <select id='record-export-range' value={preset} disabled={busy} onChange={(event) => setPreset(event.target.value)}>
                  {['today', 'hours', 'day', 'month', 'monthToNow', 'custom'].map((value) =>
                    <option key={value} value={value}>{text(value)}</option>)}
                </select>
              </Form.Field>
              {(preset === 'day' || preset === 'month') && <Form.Input label={text(preset)} type={preset === 'day' ? 'date' : 'month'}
                value={dates[preset] || ''} disabled={busy}
                onChange={(_, { value }) => setDates((current) => ({ ...current, [preset]: value }))} />}
              {preset === 'custom' && <div className='record-export-dates-v1023'>
                {['start', 'end'].map((field) => <Form.Input key={field} label={text(field)} type='datetime-local'
                  value={dates[field] || ''} disabled={busy}
                  onChange={(_, { value }) => setDates((current) => ({ ...current, [field]: value }))} />)}
              </div>}
              <Form.Field><Checkbox label={text('detailed')} checked={detailed} disabled={busy}
                onChange={(_, { checked }) => setDetailed(checked)} /></Form.Field>
            </Form>
          )}
          {job && <div className='record-export-status-v1023' role='status' aria-live='polite'>
            <strong>{text(['queued', 'running', 'completed'].includes(job.status) ? job.status : 'export_not_ready')}</strong>
            <span>{job.rows || 0}{job.total > 0 ? ` / ${job.total}` : ''} {text('rows')}</span>
          </div>}
          {error && <Message negative role='alert' content={text(error)} />}
        </Modal.Content>
        <Modal.Actions>
          <Button type='button' onClick={close}>{text(working ? 'cancel' : 'close')}</Button>
          {error && !busy ? <Button type='button' primary onClick={retry}><Icon name='redo' />{text('retry')}</Button>
            : !job && kind === 'logs' ? <Button type='button' primary loading={busy}
              disabled={busy || organizationLoading || organizationError} onClick={submit}><Icon name='download' />{text('create')}</Button>
            : job?.status === 'completed' && <Button type='button' primary loading={busy} disabled={busy} onClick={retry}>
              <Icon name='download' />{text('download')}</Button>}
        </Modal.Actions>
      </Modal>
    </>
  );
}
